import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building, CircleAlert, LogIn, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { StatusBadge } from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import {
  listTenants,
  setTenantStatus,
  statusLabels,
  tenantsKey,
  toProblem,
  type TenantSummary,
} from '../features/platform/api';
import { SlugDialog } from '../features/platform/SlugDialog';
import { TenantDrawer } from '../features/platform/TenantDrawer';
import { useTimeFormat } from '../lib/useTimeFormat';

/** The platform's tenants: create, suspend, reactivate and enter (ADR-028). */
export function TenantsPage() {
  const { request, enterTenant } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fmt = useTimeFormat();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [suspending, setSuspending] = useState<TenantSummary | null>(null);
  const [entering, setEntering] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<TenantSummary | null>(null);

  const tenants = useQuery({ queryKey: tenantsKey, queryFn: () => listTenants(request) });

  const status = useMutation({
    mutationFn: ({ t, to }: { t: TenantSummary; to: TenantSummary['status'] }) =>
      setTenantStatus(request, t.id, to),
    onSuccess: async (t) => {
      setSuspending(null);
      await qc.invalidateQueries({ queryKey: tenantsKey });
      notify(
        t.status === 'suspended'
          ? `${t.name} suspensa. Ninguém dela consegue entrar até ser reativada.`
          : `${t.name} reativada.`,
      );
    },
    onError: (err) => {
      setSuspending(null);
      notify(toProblem(err).form ?? 'Verifique os dados e tente novamente.', 'error');
    },
  });

  const enter = async (t: TenantSummary) => {
    setEntering(t.id);
    try {
      await enterTenant(t.id);
      void navigate('/', { replace: true });
    } catch (err) {
      setEntering(null);
      notify(toProblem(err).form ?? 'Não foi possível entrar na empresa.', 'error');
    }
  };

  const term = q.trim().toLowerCase();
  const rows = (tenants.data ?? []).filter(
    (t) => !term || t.name.toLowerCase().includes(term) || t.slug.includes(term),
  );

  return (
    <>
      <PageHeader
        title="Empresas"
        description="Empresas da plataforma. Entre numa empresa para administrá-la como Super administrador."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
            Nova empresa
          </Button>
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        <div role="search">
          <SearchInput label="Buscar por nome ou identificador" value={q} onChange={setQ} />
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {tenants.isError ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar as empresas"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void tenants.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : tenants.data && rows.length === 0 ? (
            <EmptyState
              icon={Building}
              title="Nenhuma empresa encontrada"
              description="Nenhuma empresa corresponde à busca."
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[760px] text-left text-[13px]"
                aria-busy={tenants.isFetching}
              >
                <caption className="sr-only">Empresas da plataforma</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Empresa
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Situação
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Fuso horário
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Usuários
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Câmeras
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Criada em
                    </th>
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!tenants.data
                    ? Array.from({ length: 4 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {[
                            'w-48',
                            'w-14',
                            'w-28',
                            'ml-auto w-6',
                            'ml-auto w-6',
                            'w-24',
                            'w-32',
                          ].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : rows.map((t) => (
                        <tr
                          key={t.id}
                          className="border-b border-line last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2 font-medium text-fg">
                              {t.name}
                              {t.platform && (
                                <span className="rounded bg-accent-soft px-1.5 py-0.5 text-2xs font-medium text-accent">
                                  Plataforma
                                </span>
                              )}
                            </div>
                            <div className="font-mono text-xs text-fg-subtle">{t.slug}</div>
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge tone={t.status === 'active' ? 'ok' : 'warn'}>
                              {statusLabels[t.status]}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-fg-muted">{t.timezone}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                            {t.users}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                            {t.cameras}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-fg-muted">
                            <time dateTime={t.created_at}>{fmt.absolute(t.created_at)}</time>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-end gap-1.5 whitespace-nowrap">
                              <Button
                                variant="ghost"
                                className="h-7"
                                aria-label={`Alterar identificador de ${t.name}`}
                                onClick={() => {
                                  setRenaming(t);
                                }}
                              >
                                Identificador
                              </Button>
                              {!t.platform && (
                                <>
                                  {t.status === 'active' ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        className="h-7"
                                        aria-label={`Suspender ${t.name}`}
                                        onClick={() => {
                                          setSuspending(t);
                                        }}
                                      >
                                        Suspender
                                      </Button>
                                      <Button
                                        className="h-7"
                                        aria-label={`Entrar em ${t.name}`}
                                        disabled={entering !== null}
                                        onClick={() => void enter(t)}
                                      >
                                        <LogIn aria-hidden="true" className="size-3.5" />
                                        {entering === t.id ? 'Entrando…' : 'Entrar'}
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      className="h-7"
                                      aria-label={`Reativar ${t.name}`}
                                      disabled={status.isPending}
                                      onClick={() => {
                                        status.mutate({ t, to: 'active' });
                                      }}
                                    >
                                      Reativar
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {creating && (
        <TenantDrawer
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {renaming && (
        <SlugDialog
          tenant={renaming}
          onClose={() => {
            setRenaming(null);
          }}
        />
      )}
      {suspending && (
        <ConfirmDialog
          title={`Suspender ${suspending.name}?`}
          confirmLabel="Suspender empresa"
          busy={status.isPending}
          onConfirm={() => {
            status.mutate({ t: suspending, to: 'suspended' });
          }}
          onCancel={() => {
            setSuspending(null);
          }}
        >
          Todas as pessoas da empresa saem do sistema na hora e não conseguem entrar até a empresa
          ser reativada. Câmeras, gravações e cadastros são mantidos.
        </ConfirmDialog>
      )}
    </>
  );
}
